import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LabelList, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useBrand } from "@/context/BrandContext";
import { formatMoney } from "@/lib/format";
import { moneyTick, colorFor, CATEGORY_COLORS } from "@/lib/charts";
import { PageHeader } from "@/components/common/PageHeader";
import { PeriodFilter } from "@/components/common/PeriodFilter";
import { StatCard } from "@/components/common/StatCard";
import { ChartCard } from "@/components/common/ChartCard";
import { Skeleton } from "@/components/ui/skeleton";

export default function ExecutiveDashboard() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const [period, setPeriod] = useState({ preset: "this_month" });

  const { data, isLoading } = useQuery({
    queryKey: ["dash-executive", brandParam, period.desde, period.hasta],
    queryFn: async () => (await api.get("/dashboards/executive", {
      params: { brand: brandParam, desde: period.desde, hasta: period.hasta },
    })).data,
  });

  return (
    <>
      <PageHeader testId="dash-executive-header" title={t("dashboards.executive.title")}
        subtitle={t("dashboards.executive.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="dash-executive-page">
        <div className="mb-4"><PeriodFilter value={period} onChange={setPeriod} /></div>

        {isLoading || !data ? (
          <div className="grid gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-[10px]" />)}
          </div>
        ) : (
          <>
            <div className="mb-4 rounded-[10px] border border-ink bg-ink p-5">
              <p className="text-[12px] text-ink-tertiary">{t("dashboards.executive.revenue_month")}</p>
              <p data-testid="dash-exec-revenue" className="mt-1 text-[36px] font-semibold tabular-nums text-white">
                {formatMoney(data.facturacion_mes_cents)}
              </p>
              {data.variacion_pct !== null && (
                <p className={`mt-1 text-[13px] font-medium ${data.variacion_pct >= 0 ? "text-state-ok" : "text-state-critical"}`}>
                  {data.variacion_pct >= 0 ? "▲" : "▼"} {Math.abs(data.variacion_pct)}%{" "}
                  {data.periodo_en_curso
                    ? t("dashboards.executive.vs_same_days", { days: data.dias_periodo })
                    : t("dashboards.executive.vs_previous")}
                </p>
              )}
            </div>

            <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <StatCard testId="dash-exec-margin"
                label={`${t("dashboards.executive.margin")} (${data.margen_pct === null ? t("wo.margin_na") : `${data.margen_pct}%`})`}
                value={formatMoney(data.margen_cents)} />
              <StatCard testId="dash-exec-closed" label={t("dashboards.executive.closed_orders")} value={data.ordenes_cerradas} />
              <StatCard testId="dash-exec-ticket" label={t("dashboards.executive.avg_ticket")} value={formatMoney(data.ticket_promedio_cents)} />
              <StatCard testId="dash-exec-unbilled" warn={data.monto_cerrado_sin_facturar_cents > 500000}
                label={t("dashboards.executive.unbilled")} value={formatMoney(data.monto_cerrado_sin_facturar_cents)} />
              {data.monto_contratado_activo_cents !== undefined && (
                <StatCard testId="dash-exec-projects-contracted"
                  label={t("projects.dashboards.active_contracted")} value={formatMoney(data.monto_contratado_activo_cents)} />
              )}
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <ChartCard testId="dash-exec-evolution" title={t("dashboards.executive.evolution")} tableView={
                <table className="w-full text-[12px]"><tbody>
                  {data.evolucion_mensual.map((m) => (
                    <tr key={m.mes} className="border-b border-line">
                      <td className="px-2 py-1">{m.mes}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(m.facturacion_cents)}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(m.costo_cents)}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(m.margen_cents)}</td>
                    </tr>
                  ))}
                </tbody></table>
              }>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.evolucion_mensual}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                    <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                    <YAxis tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                    <Tooltip formatter={(v) => formatMoney(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line isAnimationActive={false} type="monotone" dataKey="facturacion_cents" name="Facturación" stroke={CATEGORY_COLORS[0]} strokeWidth={2} dot={false} />
                    <Line isAnimationActive={false} type="monotone" dataKey="costo_cents" name="Costo" stroke={CATEGORY_COLORS[1]} strokeWidth={2} dot={false} />
                    <Line isAnimationActive={false} type="monotone" dataKey="margen_cents" name="Margen" stroke={CATEGORY_COLORS[2]} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard testId="dash-exec-top-buildings" title={t("dashboards.executive.top_buildings")} height={280} tableView={
                <table className="w-full text-[12px]"><tbody>
                  {data.top_edificios.map((e) => (
                    <tr key={e.property_nombre} className="border-b border-line">
                      <td className="px-2 py-1">{e.property_nombre}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(e.facturacion_cents)}</td>
                      <td className="px-2 py-1 text-right">{e.margen_pct === null ? t("wo.margin_na") : `${e.margen_pct}%`}</td>
                    </tr>
                  ))}
                </tbody></table>
              }>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.top_edificios} layout="vertical" margin={{ left: 90, right: 110 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                    <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                    <YAxis type="category" dataKey="property_nombre" width={85} tick={{ fontSize: 10.5, fill: "var(--ink-secondary)" }} />
                    <Tooltip formatter={(v) => formatMoney(v)} />
                    <Bar isAnimationActive={false} dataKey="facturacion_cents" fill={CATEGORY_COLORS[0]} radius={4} barSize={16}>
                      <LabelList
                        position="right"
                        style={{ fontSize: 11, fill: "var(--ink-tertiary)" }}
                        content={({ x, y, width, height, index }) => {
                          const e = data.top_edificios[index];
                          const label = `${formatMoney(e.facturacion_cents)} · ${e.margen_pct === null ? t("wo.margin_na") : `${e.margen_pct}%`}`;
                          return (
                            <text x={x + width + 6} y={y + height / 2} dy={4} fontSize={11} fill="var(--ink-tertiary)">
                              {label}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard testId="dash-exec-by-service" title={t("dashboards.executive.by_service")} height={220} tableView={
                <table className="w-full text-[12px]"><tbody>
                  {data.por_servicio.map((s) => (
                    <tr key={s.servicio} className="border-b border-line">
                      <td className="px-2 py-1">{s.servicio}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(s.monto_cents)}</td>
                    </tr>
                  ))}
                </tbody></table>
              }>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={[Object.fromEntries(data.por_servicio.map((s) => [s.servicio, s.monto_cents]))]}
                    layout="vertical">
                    <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                    <YAxis type="category" hide />
                    <Tooltip formatter={(v) => formatMoney(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {data.por_servicio.map((s) => (
                      <Bar isAnimationActive={false} key={s.servicio} dataKey={s.servicio} stackId="a" minPointSize={2} fill={colorFor(s.servicio)} radius={4} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard testId="dash-exec-by-brand" title={t("dashboards.executive.by_brand")} height={220} tableView={
                <table className="w-full text-[12px]"><tbody>
                  {data.por_marca.map((b) => (
                    <tr key={b.brand} className="border-b border-line">
                      <td className="px-2 py-1">{b.brand}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(b.facturacion_cents)}</td>
                      <td className="px-2 py-1 text-right">{formatMoney(b.margen_cents)}</td>
                    </tr>
                  ))}
                </tbody></table>
              }>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.por_marca}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                    <XAxis dataKey="brand" tick={{ fontSize: 11, fill: "var(--ink-secondary)" }} />
                    <YAxis tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                    <Tooltip formatter={(v) => formatMoney(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar isAnimationActive={false} dataKey="facturacion_cents" name="Facturación" fill={CATEGORY_COLORS[0]} radius={4} />
                    <Bar isAnimationActive={false} dataKey="margen_cents" name="Margen" fill={CATEGORY_COLORS[2]} radius={4} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </>
        )}
      </div>
    </>
  );
}
